import { type FC, type ReactNode, useEffect, useRef, useState } from "react";
import { HiOutlineCheck, HiOutlineXMark } from "react-icons/hi2";
import { cn } from "../lib/utils";

export type LabelProps = {
	size?: "x-small" | "small" | "medium" | "large" | "x-large" | number;
	bold?: boolean;
	strike?: boolean;
	iconBefore?: ReactNode;
	iconAfter?: ReactNode;
	onLabelDoubleClick?: () => void;
	children: string;
};

export type QuickEditLabelProps = Omit<LabelProps, "onLabelDoubleClick"> & {
	onTextChange: (newText: string) => void;
};

export const Label: FC<LabelProps> = ({
	size = "medium",
	bold,
	strike,
	iconBefore,
	iconAfter,
	onLabelDoubleClick,
	children,
}) => {
	const getSizeClass = (size: LabelProps["size"]) => {
		switch (size) {
			case "x-small":
				return "text-xs py-0.5 pr-2";
			case "small":
				return "text-sm py-1 pr-3";
			case "large":
				return "text-lg py-2 pr-5";
			case "x-large":
				return "text-xl py-2.5 pr-6";
			case "medium":
			default:
				return "text-base py-1.5 pr-4";
		}
	};

	return (
		<div
			onDoubleClick={onLabelDoubleClick}
			className={cn(
				"flex items-center text-gray-700",
				getSizeClass(size),
				bold && "font-bold",
				strike && "line-through text-gray-400",
			)}
		>
			{iconBefore && (
				<span data-testid="label-before-icon" className="mr-1">
					{iconBefore}
				</span>
			)}
			<span data-testid="label-text">{children}</span>
			{iconAfter && (
				<span data-testid="label-after-icon" className="ml-1">
					{iconAfter}
				</span>
			)}
		</div>
	);
};

export const QuickEditLabel: FC<QuickEditLabelProps> = ({
	size = "medium",
	bold,
	strike,
	iconBefore,
	iconAfter,
	onTextChange,
	children,
}) => {
	const inputRef = useRef<HTMLInputElement>(null);
	const [showInput, setShowInput] = useState(false);
	const [inputValue, setInputValue] = useState<string>(children);

	useEffect(() => {
		if (inputRef.current) {
			inputRef.current.focus();
		}
	}, [inputRef]);

	return showInput ? (
		<div className="flex items-center gap-1 relative bg-white border border-gray-300 rounded-md px-2">
			<input
				data-testid="quick-edit-label-input"
				name="quick-edit-label-input"
				ref={inputRef}
				value={inputValue}
				onClick={(e) => e.stopPropagation()}
				onChange={(e) => setInputValue(e.target.value)}
				className="w-full h-[26px] text-[12px] focus:outline-none"
				placeholder="New Action Item"
			/>
			<span
				title="Confirm"
				data-testid="quick-edit-label-confirm"
				className="cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					onTextChange(inputRef.current?.value || "");
					setShowInput(false);
				}}
			>
				<HiOutlineCheck color="green" size={16} />
			</span>
			<span
				title="Cancel"
				data-testid="quick-edit-label-cancel"
				className="cursor-pointer"
				onClick={(e) => {
					e.stopPropagation();
					setInputValue(children);
					setShowInput(false);
				}}
			>
				<HiOutlineXMark color="red" size={16} />
			</span>
		</div>
	) : (
		<Label
			size={size}
			bold={bold}
			strike={strike}
			iconBefore={iconBefore}
			iconAfter={iconAfter}
			onLabelDoubleClick={() => setShowInput(true)}
		>
			{children}
		</Label>
	);
};
